import { isAvatarUrl } from '../../create/helpers/createUi.helper';
import type {
  LeaderboardEntry,
  LeaderboardLivePrediction,
  MatchPrediction,
  WorldCupMatch,
  WorldCupMembershipRole,
} from '../types/worldcup.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';

const NUMERIC_ROUND_ONLY = /^\d+$/;
const ROUND_N_LABEL = /^Round\s+(\d+)$/i;

/**
 * Derives 1–2 character initials from a display name.
 * Used by adapter hooks to populate LeaderboardEntry.initials from the API's name field.
 * Example: "Carlos M." → "CM", "You" → "Y", "Kylian Mbappe Jr" → "KM"
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** Returns a human-readable countdown string until the given ISO date. */
export function formatCountdown(kickoffTime: string): string {
  const now = new Date();
  const kickoff = new Date(kickoffTime);
  const diffMs = kickoff.getTime() - now.getTime();
  if (diffMs <= 0) return '';
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Trailing tokens like Jr / Junior that are not surnames for compact scorer labels. */
const SCORER_NAME_TRAILING_SUFFIX = /^(jr\.?|jnr|junior|sr\.?|senior|ii|iii|iv)$/i;

function stripTrailingScorerSuffixes(parts: string[]): string[] {
  const out = [...parts];
  while (out.length > 1 && SCORER_NAME_TRAILING_SUFFIX.test(out[out.length - 1] ?? '')) {
    out.pop();
  }
  return out;
}

/**
 * Returns a compact scorer label (usually last name).
 * Strips trailing Jr / Jnr / Junior so "Vinicius Jnr" → "Vinicius", not "Jnr".
 */
export function scorerDisplayName(fullName: string | null): string {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const cleaned = stripTrailingScorerSuffixes(parts);
  return cleaned.length > 0 ? (cleaned[cleaned.length - 1] ?? '') : '';
}

/** Maps a raw API leaderboard entry to the shared LeaderboardEntry shape. */
export function mapLeaderboardApiEntry(entry: {
  id: string;
  userId?: string;
  role?: WorldCupMembershipRole;
  rank?: number;
  name: string;
  totalPoints: number;
  /** Provisional total (settled + live) — present on the squad leaderboard during live games. */
  provisionalPoints?: number;
  /** Live points from in-progress matches — present on the squad leaderboard. */
  livePoints?: number;
  exactHitCount: number;
  correctResultCount?: number;
  avatarId?: string | null;
  avatarConfig?: EquippedMap | null;
  avatar?: string | null;
  winnerTeamId?: string | null;
  topScorerPlayerId?: string | null;
  futuresHidden?: boolean;
  winnerCorrect: boolean | null;
  topScorerCorrect: boolean | null;
  isCurrentUser: boolean;
  livePredictions?: LeaderboardLivePrediction[];
}): LeaderboardEntry {
  const rawAvatar = entry.avatar?.trim() || undefined;
  const avatarUrl = rawAvatar && isAvatarUrl(rawAvatar) ? rawAvatar : undefined;
  const trimmedAvatarId = entry.avatarId?.trim() || null;
  const legacySlug =
    rawAvatar && !isAvatarUrl(rawAvatar) ? rawAvatar : null;
  const resolvedAvatarId = trimmedAvatarId || legacySlug;
  return {
    id: entry.id,
    userId: entry.userId,
    membershipId: entry.id,
    role: entry.role,
    rank: entry.rank ?? 0,
    name: entry.name,
    initials: getInitials(entry.name),
    // Squad sends `provisionalPoints` (settled + live) and is re-ranked by it, so
    // the headline number must match that order. Global omits it → settled total.
    points: entry.provisionalPoints ?? entry.totalPoints,
    settledPoints: entry.totalPoints,
    livePoints: entry.livePoints ?? 0,
    exactHitCount: entry.exactHitCount,
    correctResultCount: entry.correctResultCount ?? 0,
    avatarId: resolvedAvatarId,
    avatarConfig: entry.avatarConfig ?? null,
    avatarUrl,
    winnerTeamId: entry.winnerTeamId ?? null,
    topScorerPlayerId: entry.topScorerPlayerId ?? null,
    futuresHidden: entry.futuresHidden ?? false,
    winnerCorrect: entry.winnerCorrect,
    topScorerCorrect: entry.topScorerCorrect,
    trend: 'same',
    isCurrentUser: entry.isCurrentUser,
    livePredictions: entry.livePredictions,
  };
}

/**
 * Group-stage / round badge: numeric matchday from `round` or `stageLabel` is shown as `MDn`.
 * Labels like "Quarter-Finals" are returned as-is. Legacy "Round n" from the API maps to `MDn`.
 */
export function formatMatchStageBadgeLabel(
  match: Pick<WorldCupMatch, 'stageLabel' | 'round'>,
): string {
  const label = match.stageLabel?.trim() ?? '';
  const fromRoundWord = label.match(ROUND_N_LABEL);
  if (fromRoundWord) {
    return `MD${fromRoundWord[1]}`;
  }
  if (label && !NUMERIC_ROUND_ONLY.test(label)) {
    return label;
  }
  const numeric = NUMERIC_ROUND_ONLY.test(label)
    ? label
    : String(match.round ?? '').trim();
  if (NUMERIC_ROUND_ONLY.test(numeric)) {
    return `MD${numeric}`;
  }
  return label || numeric || '';
}

/** Resolves the active prediction to display: controlled input takes priority over saved prediction. */
export function resolvePrediction(
  predictionInput: { home: string; away: string } | undefined,
  savedPrediction: MatchPrediction | undefined,
): { home: string; away: string } {
  if (predictionInput != null) return predictionInput;
  return {
    home: savedPrediction?.home != null ? String(savedPrediction.home) : '',
    away: savedPrediction?.away != null ? String(savedPrediction.away) : '',
  };
}

/**
 * Returns true when the predicted outcome (win/draw/loss direction) matches the actual result.
 * Does not check for exact score — caller is responsible for excluding exact hits if needed.
 */
export function isCorrectOutcome(
  homeScore: number,
  awayScore: number,
  predHome: number,
  predAway: number,
): boolean {
  return (
    (homeScore > awayScore && predHome > predAway) ||
    (homeScore < awayScore && predHome < predAway) ||
    (homeScore === awayScore && predHome === predAway)
  );
}

/** Returns formatted match time e.g. "Jun 14 · 18:00" */
export function formatKickoffTime(kickoffTime: string): string {
  const kickoff = new Date(kickoffTime);
  const datePart = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(kickoff);
  const timePart = new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(kickoff);
  return `${datePart} · ${timePart}`;
}

/** Local kickoff clock only e.g. "18:00" (use when the list already shows the calendar date). */
export function formatKickoffClock(kickoffTime: string): string {
  const kickoff = new Date(kickoffTime);
  return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(kickoff);
}

/**
 * Match stats header line e.g. "Thu, 6/11, 22:00" (weekday, numeric date, 24h local time).
 */
export function formatMatchStatsHeaderKickoff(kickoffTime: string): string {
  const d = new Date(kickoffTime);
  const weekday = new Intl.DateTimeFormat('en', { weekday: 'short' }).format(d);
  const datePart = new Intl.DateTimeFormat('en', { month: 'numeric', day: 'numeric' }).format(d);
  const timePart = new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return `${weekday}, ${datePart}, ${timePart}`;
}

